/**
 * Client for the dev-only onboarding test account endpoints (ClickUp
 * 869fg5gm8), implemented in apps/api-py. Gated by
 * `me.capabilities.onboarding_test_accounts`; outside dev they answer 404.
 */

import { AdminApiError, adminApiUrl } from './adminApi';
import type { QuizAnswers } from './onboardingAnswers';

export interface OnboardingTemplate {
  id: string;
  name: string;
  builtin: boolean;
  segment: string;
  /** `null` only for `builtin:none` — no quiz document. */
  answers: QuizAnswers | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface CreateOnboardingAccountInput {
  answers: QuizAnswers | null;
  segment: string;
  plan_id: string | null;
  reason: string;
}

export interface OnboardingAccountResult {
  email: string;
  password: string;
  profile_id: string;
  organization_id: string;
  plan: { id: string; name: string };
  quiz: { written: boolean; answers_count: number; error: string | null };
  login_url: string;
  expires_at: string;
}

const BASE = '/api/admin/v2/onboarding-test';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(adminApiUrl(`${BASE}${path}`), {
    credentials: 'include',
    ...init,
    headers: init.body ? { 'Content-Type': 'application/json' } : undefined,
  });
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) {
    throw new AdminApiError(body.error ?? `request failed: ${res.status}`, res.status);
  }
  return body as T;
}

export async function fetchOnboardingTemplates(): Promise<OnboardingTemplate[]> {
  return (await request<{ templates: OnboardingTemplate[] }>('/templates')).templates;
}

export async function saveOnboardingTemplate(input: {
  name: string;
  segment: string;
  answers: QuizAnswers;
}): Promise<OnboardingTemplate> {
  const body = await request<{ template: OnboardingTemplate }>('/templates', {
    method: 'PUT',
    body: JSON.stringify(input),
  });
  return body.template;
}

export async function deleteOnboardingTemplate(id: string): Promise<void> {
  await request<{ ok: true }>(`/templates/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function createOnboardingAccount(
  input: CreateOnboardingAccountInput,
): Promise<OnboardingAccountResult> {
  return request<OnboardingAccountResult>('/accounts', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
